import { Component, OnInit, ViewChild, ViewChildren, QueryList, ElementRef, AfterViewInit } from '@angular/core';
import { FormControl, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { IError } from 'src/app/models/error.model';
import { setWarningText } from 'src/app/reducers/warning-popup.reducer';
import { LoginService } from 'src/app/services/login.service';

@Component({
  selector: 'app-verify-code',
  templateUrl: './verify-code.component.html',
  styleUrls: ['./verify-code.component.sass']
})
export class VerifyCodeComponent implements OnInit, AfterViewInit {
  loading = false;
  resendLoading = false;
  email = '';
  showPassword = false;
  showConfirmPassword = false;

  // Código de verificação
  codeDigits: string[] = ['', '', '', '', '', ''];
  hiddenCodeValue = '';
  activeCodeIndex = 0;
  isFocused = false;

  @ViewChildren('hiddenCodeInput') hiddenInputs!: QueryList<ElementRef<HTMLInputElement>>;

  // Formulário de senha
  passwordForm: FormGroup = new FormGroup({
    password: new FormControl('', [Validators.required, Validators.minLength(8)]),
    confirmPassword: new FormControl('', [Validators.required])
  });

  // Validações da senha
  passwordValidations = {
    hasLowercase: false,
    hasUppercase: false,
    hasMinLength: false,
    hasNumber: false,
    hasSpecialChar: false
  };

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private loginService: LoginService,
    private store: Store<{ warningPopup: IError }>
  ) {}

  ngOnInit(): void {
    // Pega o email da query param
    this.route.queryParams.subscribe(params => {
      this.email = params['email'] || '';
      if (!this.email) {
        // Se não tiver email, volta para a tela de esqueci a senha
        this.router.navigate(['/forgot-password']);
      }
    });

    // Escuta mudanças na senha para validar em tempo real
    this.passwordForm.get('password')?.valueChanges.subscribe((value: string) => {
      this.validatePassword(value);
    });
  }

  validatePassword(password: string): void {
    this.passwordValidations = {
      hasLowercase: /[a-z]/.test(password),
      hasUppercase: /[A-Z]/.test(password),
      hasMinLength: password.length >= 8,
      hasNumber: /[0-9]/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/`~';]/.test(password)
    };
  }

  isPasswordValid(): boolean {
    return Object.values(this.passwordValidations).every(v => v === true);
  }

  passwordsMatch(): boolean {
    const password = this.passwordForm.get('password')?.value;
    const confirmPassword = this.passwordForm.get('confirmPassword')?.value;
    return password && confirmPassword && password === confirmPassword;
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  navigateToLanding(): void {
    this.router.navigate(['/']);
  }

  navigateToLogin(): void {
    this.router.navigate(['/login']);
  }

  navigateToForgotPassword(): void {
    this.router.navigate(['/forgot-password']);
  }

  ngAfterViewInit(): void {
    // Foca o input invisível automaticamente após um pequeno delay
    setTimeout(() => this.focusHiddenInput('mobile'), 200);
  }

  // ---- Lógica do código de verificação (input invisível) ----

  focusHiddenInput(version: 'mobile' | 'desktop' = 'mobile'): void {
    const id = version === 'mobile' ? 'hiddenCodeInputMobile' : 'hiddenCodeInputDesktop';
    const input = document.getElementById(id) as HTMLInputElement;
    if (input) {
      input.focus();
      this.isFocused = true;
    }
  }

  onHiddenBlur(): void {
    this.isFocused = false;
  }

  onHiddenInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    // Filtra apenas dígitos e limita a 6
    const raw = input.value.replace(/[^0-9]/g, '').slice(0, 6);

    // Atualiza o valor real do input
    this.hiddenCodeValue = raw;
    input.value = raw;

    // Distribui nos boxes visuais
    for (let i = 0; i < 6; i++) {
      this.codeDigits[i] = raw[i] || '';
    }

    // Atualiza o índice ativo (cursor visual)
    this.activeCodeIndex = Math.min(raw.length, 5);
  }

  onHiddenKeyDown(event: KeyboardEvent): void {
    // Permite: Backspace, Delete, setas, Tab, Ctrl+V/Cmd+V
    const allowed = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
    if (allowed.includes(event.key)) {
      return; // deixa o browser processar normalmente
    }
    // Ctrl/Cmd + tecla (copy, paste, select all)
    if (event.ctrlKey || event.metaKey) {
      return;
    }
    // Se não é número, bloqueia
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
      return;
    }
    // Se já tem 6 dígitos, bloqueia
    if (this.hiddenCodeValue.length >= 6) {
      event.preventDefault();
    }
  }

  isCodeComplete(): boolean {
    return this.codeDigits.every(digit => digit !== '');
  }

  getFullCode(): string {
    return this.codeDigits.join('');
  }

  canSubmit(): boolean {
    return this.isCodeComplete() && this.isPasswordValid() && this.passwordsMatch();
  }

  onSubmit(): void {
    if (!this.isCodeComplete()) {
      this.store.dispatch(setWarningText('Por favor, digite o código completo.'));
      return;
    }

    if (!this.isPasswordValid()) {
      this.store.dispatch(setWarningText('A senha não atende aos requisitos mínimos.'));
      return;
    }

    if (!this.passwordsMatch()) {
      this.store.dispatch(setWarningText('As senhas não coincidem.'));
      return;
    }

    this.loading = true;
    const code = this.getFullCode();
    const newPassword = this.passwordForm.get('password')?.value;

    this.loginService.resetPassword(this.email, code, newPassword)
      .then(() => {
        this.store.dispatch(setWarningText('Senha alterada com sucesso!'));
        this.router.navigate(['/login']);
      })
      .catch((error: Error) => {
        this.store.dispatch(setWarningText(error?.message || 'Erro ao alterar senha. Tente novamente.'));
        // Limpa os inputs de código
        this.codeDigits = ['', '', '', '', '', ''];
        this.hiddenCodeValue = '';
        this.activeCodeIndex = 0;
        this.focusHiddenInput();
      })
      .finally(() => {
        this.loading = false;
      });
  }

  resendCode(): void {
    this.resendLoading = true;

    this.loginService.forgotPassword(this.email)
      .then(() => {
        this.store.dispatch(setWarningText('Código reenviado com sucesso!'));
      })
      .catch((error: Error) => {
        this.store.dispatch(setWarningText(error?.message || 'Erro ao reenviar código. Tente novamente.'));
      })
      .finally(() => {
        this.resendLoading = false;
      });
  }
}
